import React, { useState, useEffect } from "react";
import { Calendar, MultiViewCalendar, DatePicker } from "@progress/kendo-react-dateinputs";
import { Popover } from "@progress/kendo-react-tooltip";
import { Button, DropDownButton } from "@progress/kendo-react-buttons";
import { ShortDateFormat } from "../../../../shared/helpers/DateHelpers";

const SEGMENT_DEFS = [
    { start: 0, end: 2, maxLen: 2 },  // month
    { start: 3, end: 5, maxLen: 2 },  // day
    { start: 6, end: 10, maxLen: 4 }, // year
];

export const FirefoxDateFilterHeaderCell = (props) => {
    const [showCalendar, setShowCalendar] = useState(false);
    const [calendarValue, setCalendarValue] = useState(null);
    const [filterType, setFilterType] = useState("eq");
    const [segmentValues, setSegmentValues] = useState(['', '', '']);
    const [activeSegment, setActiveSegment] = useState(0);

    const anchor = React.useRef(null);
    const inputRef = React.useRef(null);

    function buildDisplayValue(segs) {
        const m = segs[0] ? segs[0].padStart(2, '0') : 'month';
        const d = segs[1] ? segs[1].padStart(2, '0') : 'day';
        const y = segs[2] ? segs[2].padStart(4, '0') : 'year';
        return `${m}/${d}/${y}`;
    }

    useEffect(() => {
        if (inputRef.current && document.activeElement === inputRef.current) {
            const seg = SEGMENT_DEFS[activeSegment];
            inputRef.current.setSelectionRange(seg.start, seg.end);
        }
    }, [activeSegment, segmentValues]);

    const filterTypeOptions = [
        { text: "Is equal to",            operator: "eq" },
        { text: "Is not equal to",         operator: "neq" },
        { text: "Is after",               operator: "gt" },
        { text: "Is after or equal to",   operator: "gte" },
        { text: "Is before",              operator: "lt" },
        { text: "Is before or equal to",  operator: "lte" },
    ];

    function handleFilterTypeSelect(e) {
        setFilterType(e.item.operator);
    }

    function selectedCalendarCallback(event) {
        const date = event.value;
        if (date) {
            setSegmentValues([
                String(date.getMonth() + 1),
                String(date.getDate()),
                String(date.getFullYear()),
            ]);
        }
        setCalendarValue(date);
        setShowCalendar(false);
        props.onChange({
            value: date,
            operator: filterType,
            syntheticEvent: event.syntheticEvent,
        });
    }

    function handleClearDateFilter(event) {
        setCalendarValue(null);
        setSegmentValues(['', '', '']);
        setActiveSegment(0);
        props.onChange({
            value: null,
            operator: "",
            syntheticEvent: event,
        });
    }

    function tryApplyFilter(segs, event) {
        const [m, d, y] = segs;
        if (!m || !d || !y || y.length < 4) return;
        const parsed = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
        if (!isNaN(parsed.getTime())) {
            setCalendarValue(parsed);
            props.onChange({ value: parsed, operator: filterType, syntheticEvent: event });
        }
    }

    function handleInputClick(event) {
        const pos = event.target.selectionStart;
        const segIndex = pos <= 2 ? 0 : pos <= 5 ? 1 : 2;
        setActiveSegment(segIndex);
    }

    function handleInputFocus(event) {
        const seg = SEGMENT_DEFS[activeSegment];
        event.target.setSelectionRange(seg.start, seg.end);
    }

    function handleInputKeyDown(event) {
        const key = event.key;

        if (key === 'ArrowRight') {
            event.preventDefault();
            if (activeSegment < 2) setActiveSegment(activeSegment + 1);
            return;
        }
        if (key === 'ArrowLeft') {
            event.preventDefault();
            if (activeSegment > 0) setActiveSegment(activeSegment - 1);
            return;
        }
        if (key === 'Backspace' || key === 'Delete') {
            event.preventDefault();
            const newSegs = [...segmentValues];
            newSegs[activeSegment] = '';
            setSegmentValues(newSegs);
            return;
        }
        if (key === 'Enter') {
            event.preventDefault();
            tryApplyFilter(segmentValues, event);
            return;
        }
        if (key === 'Tab') {
            return; // allow natural tab navigation
        }
        if (/^\d$/.test(key)) {
            event.preventDefault();
            const def = SEGMENT_DEFS[activeSegment];
            const current = segmentValues[activeSegment];
            const newVal = current.length >= def.maxLen ? key : current + key;
            const newSegs = [...segmentValues];
            newSegs[activeSegment] = newVal;
            setSegmentValues(newSegs);

            const shouldAdvance =
                newVal.length >= def.maxLen ||
                (activeSegment === 0 && newVal.length === 1 && parseInt(newVal) > 1) ||
                (activeSegment === 1 && newVal.length === 1 && parseInt(newVal) > 3);

            if (shouldAdvance && activeSegment < 2) {
                setActiveSegment(activeSegment + 1);
            }
            tryApplyFilter(newSegs, event);
            return;
        }
        event.preventDefault();
    }

    return (
        <td className="noPadding" style={props.style ? props.style : undefined}>
            <div className="k-filtercell-wrapper"
                style={{
                    display: "grid",
                    justifyItems: "center"
                }}>
                <div className={"k-textbox k-input k-input-md k-input-solid k-rounded-md"}>
                    <input
                        ref={inputRef}
                        className={"k-input-inner"}
                        value={buildDisplayValue(segmentValues)}
                        onChange={() => {}}
                        onClick={handleInputClick}
                        onFocus={handleInputFocus}
                        onKeyDown={handleInputKeyDown}
                    />
                    <Button
                        className={"k-button k-button-md k-button-solid k-button-solid-base k-icon-button k-input-button"}
                        onClick={() => setShowCalendar(!showCalendar)} ref={anchor}
                    >
                        <i className={"fa fa-calendar"} />
                    </Button>
                    <Popover
                        show={showCalendar}
                        anchor={anchor.current && anchor.current.element}
                        position={"bottom"}
                    >
                        <MultiViewCalendar onChange={selectedCalendarCallback} value={calendarValue} views={1} />
                    </Popover>
                </div>
                <div style={{ justifySelf: "start" }}>
                    <DropDownButton
                        icon="filter"
                        themeColor="base"
                        fillMode="solid"
                        rounded="md"
                        size="medium"
                        title={filterTypeOptions.find(o => o.operator === filterType).text ? "Filter" : ""}
                        items={filterTypeOptions}
                        textField="text"
                        onItemClick={handleFilterTypeSelect}
                        className="k-icon-button"
                        itemRender={(itemProps) => (
                            <span style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontWeight: itemProps.item.operator === filterType ? "bold" : "normal",
                            }}>
                                <span style={{ width: "14px", flexShrink: 0 }}>
                                    {itemProps.item.operator === filterType && (
                                        <span className="k-icon k-i-check" />
                                    )}
                                </span>
                                {itemProps.item.text}
                            </span>
                        )}
                    />
                    <Button
                        title="Clear Filter"
                        type="button"
                        icon="filter-clear"
                        themeColor="base"
                        className="k-button-md k-rounded-md k-icon-button"
                        disabled={!calendarValue}
                        onClick={handleClearDateFilter}
                    />
                </div>
            </div>
        </td>

    );
};